// frontend/src/components/client/ClientFuelRequests.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Fuel, Calendar, Plane, X, CheckCircle, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import { getFuelRequests, createFuelRequest, cancelFuelRequest, getMyAircraft } from '../../services/clientApi';
import { useClientAuth } from '../../contexts/ClientAuthContext';

const StatusBadge = ({ status }) => {
  const config = {
    pending: { label: 'En attente', color: 'bg-yellow-500/20 text-yellow-400' },
    approved: { label: 'Approuvé', color: 'bg-green-500/20 text-green-400' },
    completed: { label: 'Terminé', color: 'bg-blue-500/20 text-blue-400' },
    rejected: { label: 'Refusé', color: 'bg-red-500/20 text-red-400' },
    cancelled: { label: 'Annulé', color: 'bg-gray-500/20 text-gray-400' }
  };
  const { label, color } = config[status] || config.pending;
  return <span className={`px-2 py-1 rounded-full text-xs ${color}`}>{label}</span>;
};

const ClientFuelRequests = () => {
  const { client } = useClientAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [aircraftList, setAircraftList] = useState([]);
  const [formData, setFormData] = useState({
    aircraft_id: '',
    quantity: '',
    scheduled_date: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const intervalRef = useRef(null);

  const loadRequests = useCallback(async (showRefreshAnimation = false) => {
    if (showRefreshAnimation) setRefreshing(true);
    setLoading(true);
    try {
      const response = await getFuelRequests();
      setRequests(response.data);
    } catch (error) {
      console.error('Error loading fuel requests:', error);
    } finally {
      setLoading(false);
      if (showRefreshAnimation) setRefreshing(false);
    }
  }, []);

  const loadAircraft = async () => {
    try {
      const response = await getMyAircraft();
      setAircraftList(response.data);
    } catch (error) {
      console.error('Error loading aircraft:', error);
    }
  };

  // Rafraîchissement manuel
  const handleRefresh = () => {
    loadRequests(true);
  };

  // Rafraîchissement automatique toutes les 60 secondes
  useEffect(() => {
    loadRequests();
    loadAircraft();
    
    // Mettre en place l'intervalle de rafraîchissement
    intervalRef.current = setInterval(() => {
      loadRequests();
    }, 60000); // 60 secondes
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [loadRequests]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.aircraft_id || !formData.quantity || !formData.scheduled_date) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      await createFuelRequest(formData);
      setShowModal(false);
      setFormData({ aircraft_id: '', quantity: '', scheduled_date: '', description: '' });
      await loadRequests();
    } catch (error) {
      console.error('Error creating fuel request:', error);
      alert('Erreur lors de la création de la demande');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    if (window.confirm('Annuler cette demande ?')) {
      try {
        await cancelFuelRequest(id);
        await loadRequests();
      } catch (error) {
        console.error('Error cancelling fuel request:', error);
        alert('Erreur lors de l\'annulation');
      }
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div>
      {/* Header avec bouton refresh */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-white">Demandes de carburant</h2>
          <p className="text-gray-400 text-sm">Gérez vos demandes de fuel</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition disabled:opacity-50"
            title="Rafraîchir"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nouvelle demande
          </button>
        </div>
      </div>

      {/* Indicateur de rafraîchissement automatique */}
      <div className="text-right mb-2">
        <span className="text-xs text-gray-500">Actualisation automatique toutes les 60 secondes</span>
      </div>

      {/* Liste des demandes */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 bg-gray-800/50 rounded-xl border border-gray-700">
          <Fuel className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500">Aucune demande de carburant</p>
          <button
            onClick={() => setShowModal(true)}
            className="mt-3 text-blue-400 hover:text-blue-300 text-sm"
          >
            Créer une demande
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => (
            <div key={req.id} className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500/20 rounded-lg">
                    <Fuel className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{req.quantity} gallons</span>
                      <StatusBadge status={req.status} />
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                      {req.tail_number} - {req.manufacturer} {req.model}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(req.scheduled_date)}
                      </span>
                    </div>
                    {req.description && (
                      <p className="text-xs text-gray-500 mt-2">{req.description}</p>
                    )}
                  </div>
                </div>
                {req.status === 'pending' && (
                  <button
                    onClick={() => handleCancel(req.id)}
                    className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 transition"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de création */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold">Nouvelle demande de carburant</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Avion *</label>
                <select
                  value={formData.aircraft_id}
                  onChange={(e) => setFormData({ ...formData, aircraft_id: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  required
                >
                  <option value="">Sélectionner un avion</option>
                  {aircraftList.map((a) => (
                    <option key={a.id} value={a.id}>{a.tail_number} - {a.manufacturer} {a.model}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Quantité (gallons) *</label>
                <input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Date souhaitée *</label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="2"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white resize-none"
                  placeholder="Informations complémentaires..."
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white font-medium disabled:opacity-50"
                >
                  {submitting ? 'Envoi...' : 'Envoyer la demande'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientFuelRequests;